/* Floor Calculator (Impulse-safe)
  FIX (2026-02-23):
   - Fix money parsing (variant JSON vs dataset vs DOM)
   - Ensure all unit outputs render (€/m², €/lfm, €/Stück, Paketpreis)
   - Remove Verschnitt from Sockelleisten block completely (UI + logic)

  FIX (2026-02-25):
   - Add HARD bundle binding for calculator lines across cart + drawer:
      * session bundle id per product page (sessionStorage)
      * set _fc_parent + _fc_pid on ALL calculator lines (floor + deps + modal addons)
      * add _fc_role: main | dep | addon for strict drawer sync (no heuristics)
      * add _fc_packlike_m2 for packlike Trittschall to allow accurate drawer sync
   - Upsert in cart by composite signature:
      (variant_id + _fc_parent + _fc_pid + _fc_group + _fc_role)
     to prevent overwriting add-ons from other product pages and to avoid collisions
*/

(() => {
  'use strict';

  const SELECTORS = {
    root: '#floor-calculator[data-fc="root"]',
    variantJsonTextarea: 'textarea[data-current-variant-json]',
    productForm: 'form[action^="/cart/add"]',
    qtyInput: 'input[name="quantity"]',

    atcButtonInForm: 'button[type="submit"][name="add"], button[type="submit"][data-add-to-cart], button.add-to-cart',
    atcButtonInRoot: 'button[name="add"], button[data-add-to-cart], button.add-to-cart, button[type="submit"]',

    // inputs
    m2Input: '[data-fc-input="m2"]',
    verschnitt: '[data-fc-input="verschnitt"]',
    lfmInput: '[data-fc-input="lfm"]',
    trittM2Input: '[data-fc-input="tritt_m2"]',
    kleber6QtyInput: '[data-fc-input="kleber6_qty"]',
    kleber14QtyInput: '[data-fc-input="kleber14_qty"]',

    // outputs meta
    outPakete: '[data-fc-out="pakete"]',
    outPackageSize: '[data-fc-out="package_size"]',
    outPaketPrice: '[data-fc-out="paket_price"]',

    // ✅ unit price outputs (these exist in Liquid)
    outFloorUnitPrice: '[data-fc-out="floor_unit_price"]',
    outSockelUnitPrice: '[data-fc-out="sockel_unit_price"]',
    outTrittUnitPrice: '[data-fc-out="tritt_unit_price"]',
    outKleber6UnitPrice: '[data-fc-out="kleber6_unit_price"]',
    outKleber14UnitPrice: '[data-fc-out="kleber14_unit_price"]',

    // ✅ Trittschall meta outputs
    outTrittPackageSize: '[data-fc-out="tritt_package_size"]',
    outTrittPaketPrice: '[data-fc-out="tritt_paket_price"]',

    // totals
    outFloorTotal: '[data-fc-out="floor_total"]',
    outSockelTotal: '[data-fc-out="sockel_total"]',
    outTrittTotal: '[data-fc-out="tritt_total"]',
    outKleber6Total: '[data-fc-out="kleber6_total"]',
    outKleber14Total: '[data-fc-out="kleber14_total"]',
    outFloorBlockTotal: '[data-fc-out="floor_block_total"]',
    outSockelBlockTotal: '[data-fc-out="sockel_block_total"]',
    outTrittBlockTotal: '[data-fc-out="tritt_block_total"]',
    outKleber6BlockTotal: '[data-fc-out="kleber6_block_total"]',
    outKleber14BlockTotal: '[data-fc-out="kleber14_block_total"]',
    outGrandTotal: '[data-fc-out="grand_total"]',
    outGrandCompareTotal: '[data-fc-out="grand_compare_total"]',
    outSavings: '[data-fc-out="savings"]',

    // UVP outputs
    outFloorUvp: '[data-fc-out="floor_uvp"]',
    outSockelUvp: '[data-fc-out="sockel_uvp"]',
    outTrittUvp: '[data-fc-out="tritt_uvp"]',
    outKleber6Uvp: '[data-fc-out="kleber6_uvp"]',
    outKleber14Uvp: '[data-fc-out="kleber14_uvp"]',

    // qty outputs
    outSockelQty: '[data-fc-out="sockel_qty"]',
    outTrittPakete: '[data-fc-out="tritt_pakete"]',
    outKleber6Qty: '[data-fc-out="kleber6_qty"]',
    outKleber14Qty: '[data-fc-out="kleber14_qty"]',

    // savings per block (EUR + %)
    outFloorSaveEur: '[data-fc-out="floor_save_eur"]',
    outSockelSaveEur: '[data-fc-out="sockel_save_eur"]',
    outTrittSaveEur: '[data-fc-out="tritt_save_eur"]',
    outKleber6SaveEur: '[data-fc-out="kleber6_save_eur"]',
    outKleber14SaveEur: '[data-fc-out="kleber14_save_eur"]',

    // blocks
    blockSockel: '[data-fc-block="sockel"]',
    blockTritt: '[data-fc-block="trittschall"]',
    blockKleber: '[data-fc-block="kleber"]',
    blockKleber6: '[data-fc-block="kleber6"]',
    blockKleber14: '[data-fc-block="kleber14"]',
    blockAddonsSockel: '[data-fc-block="addons_sockel"]',
    blockAddonsVerlege: '[data-fc-block="addons_verlege"]',

    // actions
    actionBtn: '[data-fc-action]',
    removeBtn: '[data-fc-remove]',
    restoreBtn: '[data-fc-restore]',

    // addons selections output
    outAddonsSockelSelected: '[data-fc-out="addons_sockel_selected"]',
    outAddonsVerlegeSelected: '[data-fc-out="addons_verlege_selected"]',

    // modal
    modal: '[data-fc-modal]',
    modalClose: '[data-fc-modal-close]',
    modalTitle: '[data-fc-modal-title]',
    modalSubtitle: '[data-fc-modal-subtitle]',
    modalSearch: '[data-fc-modal-search]',
    modalGrid: '[data-fc-modal-grid]',
    modalHint: '[data-fc-modal-hint]',
    modalApply: '[data-fc-modal-apply]',

    accessoriesJson: '[data-fc-accessories-json]',

    // ✅ Impulse price block fallbacks:
    domPrice: '[data-product-price]',
    domCompare: '[data-compare-price]',
  };

  const DEFAULTS = {
    verschnittRate: 0.05,
    kleberGPerM2Avg: 300,
    kleberPackKg6: 6,
    kleberPackKg14: 14,
  };

  function clampNumber(n, min, max) {
    const x = Number.isFinite(n) ? n : 0;
    return Math.min(Math.max(x, min), max);
  }

  function ceilInt(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.ceil(x));
  }

  function roundTo(n, decimals = 2) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    const m = Math.pow(10, decimals);
    return Math.round(x * m) / m;
  }

  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch (_) { return null; }
  }

  function textIncludes(hay, needle) {
    if (!hay || !needle) return false;
    return String(hay).toLowerCase().includes(String(needle).toLowerCase());
  }

  function getCurrencyFromShopify() {
    return (window.Shopify && window.Shopify.currency && (window.Shopify.currency.active || window.Shopify.currency.currency)) || 'EUR';
  }

  function formatMoneyFromCents(cents, currency = 'EUR', locale = document.documentElement.lang || 'de-DE') {
    const c = Number(cents);
    const safeCents = Number.isFinite(c) ? c : 0;
    const value = safeCents / 100;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
    } catch (_) {
      return `${value.toFixed(2)} €`;
    }
  }

  function formatNumber(n, maxFrac = 2, locale = document.documentElement.lang || 'de-DE') {
    const x = Number(n);
    if (!Number.isFinite(x)) return '0';
    try {
      return new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: maxFrac }).format(x);
    } catch (_) {
      return String(x);
    }
  }

  // ✅ Money parser: handles cents, "46,51", "1.234,56", "46.51"
  function parseMoneyToCents(input) {
    if (input == null) return 0;

    if (typeof input === 'number' && Number.isFinite(input)) {
      if (Number.isInteger(input) && input >= 100) return input;      // cents
      if (input < 1000) return Math.round(input * 100);               // euros
      return Math.round(input);
    }

    const str = String(input).replace(/\s+/g, '').replace(/[^\d,.\-]/g, '');
    if (!str) return 0;

    let normalized = str;
    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');

    if (hasComma && hasDot) {
      normalized = normalized.replace(/\./g, '').replace(',', '.'); // 1.234,56 -> 1234.56
    } else if (hasComma && !hasDot) {
      normalized = normalized.replace(',', '.'); // 46,51 -> 46.51
    }

    const num = Number(normalized);
    if (!Number.isFinite(num)) return 0;

    if (Number.isInteger(num) && num >= 100) return num; // cents-like "4185"
    return Math.round(num * 100); // euros
  }

  // -----------------------
  // ✅ Bundle ID per product page (session)
  // -----------------------
  function fcBundleStorageKey(productId, handle) {
    const pid = String(productId || '').trim();
    const h = String(handle || '').trim();
    return `fc_bundle:${pid || h || '0'}`;
  }

  function fcMakeBundleId() {
    try {
      if (crypto && typeof crypto.randomUUID === 'function') return `fc_${crypto.randomUUID()}`;
    } catch (_) {}
    return `fc_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  function fcGetOrCreateBundleId(productId, handle) {
    const key = fcBundleStorageKey(productId, handle);
    let id = '';
    try { id = sessionStorage.getItem(key) || ''; } catch (_) {}
    if (!id) {
      id = fcMakeBundleId();
      try { sessionStorage.setItem(key, id); } catch (_) {}
    }
    return id;
  }

  function openMiniCart() {
    const t = window.theme;

    try {
      if (t && typeof t.CartDrawer === 'function') {
        if (!t.__fcCartDrawerInstance) {
          t.__fcCartDrawerInstance = new t.CartDrawer();
          if (typeof t.__fcCartDrawerInstance.init === 'function') {
            try { t.__fcCartDrawerInstance.init(); } catch (_) {}
          }
        }
        if (t.__fcCartDrawerInstance && typeof t.__fcCartDrawerInstance.open === 'function') {
          t.__fcCartDrawerInstance.open();
          return true;
        }
      }
    } catch (_) {}

    const triggers = [
      '[aria-controls="CartDrawer"]',
      '.js-drawer-open-cart',
      '.site-nav__link--icon[aria-controls="CartDrawer"]'
    ];

    for (const sel of triggers) {
      const el = document.querySelector(sel);
      if (el && typeof el.click === 'function') {
        try { el.click(); return true; } catch (_) {}
        try {
          el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
          return true;
        } catch (_) {}
      }
    }

    ['theme:cart:open','cart:open','drawer:open','ajaxProduct:added','cart:refresh','cart:updated'].forEach((name) => {
      try { document.dispatchEvent(new CustomEvent(name, { bubbles: true })); } catch (_) {}
    });

    return false;
  }

  function computeBestGlueCombo(targetKg, price6Cents, price14Cents) {
    const kg6 = DEFAULTS.kleberPackKg6;
    const kg14 = DEFAULTS.kleberPackKg14;

    if (targetKg <= 0) return { q6: 0, q14: 0, totalKg: 0 };

    const max14 = Math.ceil(targetKg / kg14) + 2;
    const max6 = Math.ceil(targetKg / kg6) + 3;

    let best = null;

    for (let q14 = 0; q14 <= max14; q14++) {
      for (let q6 = 0; q6 <= max6; q6++) {
        const totalKg = q14 * kg14 + q6 * kg6;
        if (totalKg < targetKg) continue;

        const cost = q14 * (price14Cents || 0) + q6 * (price6Cents || 0);
        const over = totalKg - targetKg;

        const cand = { q6, q14, totalKg, cost, over };

        if (!best) { best = cand; continue; }

        if (cand.cost < best.cost) best = cand;
        else if (cand.cost === best.cost && cand.over < best.over) best = cand;
        else if (cand.cost === best.cost && cand.over === best.over) {
          const packs = cand.q6 + cand.q14;
          const bestPacks = best.q6 + best.q14;
          if (packs < bestPacks) best = cand;
        }
      }
    }

    if (!best) return { q6: 0, q14: 0, totalKg: 0 };
    return { q6: best.q6, q14: best.q14, totalKg: best.totalKg };
  }

  // -------- CART UPSERT HELPERS (anti-duplicates) ----------
  async function fetchCartJson() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`Failed /cart.js (${res.status})`);
    return res.json();
  }

  async function cartChangeByKey(lineKey, quantity, properties) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: Number(quantity) || 0, properties: properties || {} })
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status} (change): ${txt}`);
    }
    return res.json();
  }

  async function cartAddItems(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status} (add): ${txt}`);
    }
    return res.json();
  }

  class FloorCalculator {
    constructor(root) {
      this.root = root;
      this.currency = getCurrencyFromShopify();

      this.productId = root.dataset.productId || '';
      this.handle = root.dataset.productHandle || '';
      this.initialVariantId = Number(root.dataset.initialVariantId || 0) || 0;

      // ✅ stable per-product bundle id (session)
      this.bundleId = fcGetOrCreateBundleId(this.productId, this.handle);

      this.packageSize = Number(String(root.dataset.packageSize || '0').replace(',', '.')) || 0;

      this.artDesBodens = (root.dataset.artDesBodens || '').trim();
      this.verlegeart = (root.dataset.verlegeart || '').trim();
      this.hasIntegratedTritt = String(root.dataset.hasIntegratedTrittschall || '0') === '1';
      this.verschnittRate = Number(root.dataset.verschnittRate || 0.05) || 0.05;

      // ✅ dataset prices (likely cents) but normalize anyway
      this.initialVariantPriceCents = parseMoneyToCents(root.dataset.initialVariantPrice || 0);
      this.initialVariantCompareAtCents = parseMoneyToCents(root.dataset.initialVariantCompareAt || 0);

      this.sockelVariantId = Number(root.dataset.sockelVariantId || 0) || 0;
      this.sockelPriceCents = parseMoneyToCents(root.dataset.sockelPrice || 0);
      this.sockelLfmPerStueck = Number(String(root.dataset.sockelLfmPerStueck || '0').replace(',', '.')) || 0;

      // GRATIS SOCKEL PROMO: eligibility flag set from Liquid in product-floor-calculator.liquid.
      // When true: floor cart line gets _role=floor + _floor_base=promo stamped, AND the paid
      // sockel sub-item below is NOT pushed (sockelleisten-promo.js handles the Gratis one).
      this.isPromoEligible = String(root.dataset.promoEligible || '0') === '1';
      this.promoSrcCollection = String(root.dataset.promoSrcCollection || '').trim();

      this.trittVariantId = Number(root.dataset.trittVariantId || 0) || 0;
      this.trittPackPriceCents = parseMoneyToCents(root.dataset.trittPrice || 0);
      this.trittPkgM2 = Number(String(root.dataset.trittPkgM2 || '0').replace(',', '.')) || 0;

      this.trittMode = String(root.dataset.trittMode || 'pack').trim(); // 'pack' | 'packlike'
      this.trittPacklikeM2 = Number(String(root.dataset.trittPacklikeM2 || '0').replace(',', '.')) || 0; // e.g. 25

      this.kleber6VariantId = Number(root.dataset.kleber6VariantId || 0) || 0;
      this.kleber6PriceCents = parseMoneyToCents(root.dataset.kleber6Price || 0);

      this.kleber14VariantId = Number(root.dataset.kleber14VariantId || 0) || 0;
      this.kleber14PriceCents = parseMoneyToCents(root.dataset.kleber14Price || 0);

      // UVP compare-at
      this.floorCompareAtCents = this.initialVariantCompareAtCents || 0;
      this.sockelCompareAtCents = parseMoneyToCents(root.dataset.sockelCompareAt || 0);
      this.trittCompareAtCents = parseMoneyToCents(root.dataset.trittCompareAt || 0);
      this.kleber6CompareAtCents = parseMoneyToCents(root.dataset.kleber6CompareAt || 0);
      this.kleber14CompareAtCents = parseMoneyToCents(root.dataset.kleber14CompareAt || 0);

      if (!this.kleber6CompareAtCents) this.kleber6CompareAtCents = 4299;
      if (!this.kleber14CompareAtCents) this.kleber14CompareAtCents = 8900;

      this.isSyncing = false;
      this.currentVariant = null;

      // category applicability flags
      this.showTritt = true;

      // inputs
      this.m2Input = root.querySelector(SELECTORS.m2Input);
      this.verschnittInput = root.querySelector(SELECTORS.verschnitt);
      this.lfmInput = root.querySelector(SELECTORS.lfmInput);
      this.trittM2Input = root.querySelector(SELECTORS.trittM2Input);
      this.kleber6QtyInput = root.querySelector(SELECTORS.kleber6QtyInput);
      this.kleber14QtyInput = root.querySelector(SELECTORS.kleber14QtyInput);

      // outputs
      this.outPakete = root.querySelector(SELECTORS.outPakete);
      this.outPackageSize = root.querySelector(SELECTORS.outPackageSize);
      this.outPaketPrice = root.querySelector(SELECTORS.outPaketPrice);

      this.outFloorUnitPrice = root.querySelector(SELECTORS.outFloorUnitPrice);
      this.outSockelUnitPrice = root.querySelector(SELECTORS.outSockelUnitPrice);
      this.outTrittUnitPrice = root.querySelector(SELECTORS.outTrittUnitPrice);
      this.outKleber6UnitPrice = root.querySelector(SELECTORS.outKleber6UnitPrice);
      this.outKleber14UnitPrice = root.querySelector(SELECTORS.outKleber14UnitPrice);

      this.outTrittPackageSize = root.querySelector(SELECTORS.outTrittPackageSize);
      this.outTrittPaketPrice = root.querySelector(SELECTORS.outTrittPaketPrice);

      this.outFloorTotal = root.querySelector(SELECTORS.outFloorTotal);
      this.outSockelTotal = root.querySelector(SELECTORS.outSockelTotal);
      this.outTrittTotal = root.querySelector(SELECTORS.outTrittTotal);
      this.outKleber6Total = root.querySelector(SELECTORS.outKleber6Total);
      this.outKleber14Total = root.querySelector(SELECTORS.outKleber14Total);

      this.outGrandTotal = root.querySelector(SELECTORS.outGrandTotal);
      this.outGrandCompareTotal = root.querySelector(SELECTORS.outGrandCompareTotal);
      this.outSavings = root.querySelector(SELECTORS.outSavings);

      this.outFloorUvp = root.querySelector(SELECTORS.outFloorUvp);
      this.outSockelUvp = root.querySelector(SELECTORS.outSockelUvp);
      this.outTrittUvp = root.querySelector(SELECTORS.outTrittUvp);
      this.outKleber6Uvp = root.querySelector(SELECTORS.outKleber6Uvp);
      this.outKleber14Uvp = root.querySelector(SELECTORS.outKleber14Uvp);

      this.outSockelQty = root.querySelector(SELECTORS.outSockelQty);
      this.outTrittPakete = root.querySelector(SELECTORS.outTrittPakete);
      this.outKleber6Qty = root.querySelector(SELECTORS.outKleber6Qty);
      this.outKleber14Qty = root.querySelector(SELECTORS.outKleber14Qty);

      // Per-block totals ("Gesamt:" per block)
      this.outFloorBlockTotal = root.querySelector(SELECTORS.outFloorBlockTotal);
      this.outSockelBlockTotal = root.querySelector(SELECTORS.outSockelBlockTotal);
      this.outTrittBlockTotal = root.querySelector(SELECTORS.outTrittBlockTotal);
      this.outKleber6BlockTotal = root.querySelector(SELECTORS.outKleber6BlockTotal);
      this.outKleber14BlockTotal = root.querySelector(SELECTORS.outKleber14BlockTotal);

      // Saving totals ("Sie sparen:" per block)
      this.outFloorSaveEur = root.querySelector(SELECTORS.outFloorSaveEur);
      this.outSockelSaveEur = root.querySelector(SELECTORS.outSockelSaveEur);
      this.outTrittSaveEur = root.querySelector(SELECTORS.outTrittSaveEur);
      this.outKleber6SaveEur = root.querySelector(SELECTORS.outKleber6SaveEur);
      this.outKleber14SaveEur = root.querySelector(SELECTORS.outKleber14SaveEur);

      // blocks
      this.blockSockel = root.querySelector(SELECTORS.blockSockel);
      this.blockTritt = root.querySelector(SELECTORS.blockTritt);
      this.blockKleber = root.querySelector(SELECTORS.blockKleber);
      this.blockKleber6 = root.querySelector(SELECTORS.blockKleber6);
      this.blockKleber14 = root.querySelector(SELECTORS.blockKleber14);
      this.blockAddonsSockel = root.querySelector(SELECTORS.blockAddonsSockel);
      this.blockAddonsVerlege = root.querySelector(SELECTORS.blockAddonsVerlege);

      this.outAddonsSockelSelected = root.querySelector(SELECTORS.outAddonsSockelSelected);
      this.outAddonsVerlegeSelected = root.querySelector(SELECTORS.outAddonsVerlegeSelected);

      // Modal
      this.modal = root.querySelector(SELECTORS.modal);
      this.modalTitle = root.querySelector(SELECTORS.modalTitle);
      this.modalSubtitle = root.querySelector(SELECTORS.modalSubtitle);
      this.modalSearch = root.querySelector(SELECTORS.modalSearch);
      this.modalGrid = root.querySelector(SELECTORS.modalGrid);
      this.modalHint = root.querySelector(SELECTORS.modalHint);
      this.modalApply = root.querySelector(SELECTORS.modalApply);

      this.activeModalType = null;
      this.modalItems = [];
      this.modalQty = new Map();

      this.accessoriesCatalog = { sockel: [], verlege: [] };
      this.accessoriesByVariant = new Map();

      this.addonUndoKeySockel = null;
      this.addonUndoKeyVerlege = null;

      // Impulse form
      this.form = this.findImpulseProductForm();
      this.qtyInput = this.form ? this.form.querySelector(SELECTORS.qtyInput) : null;

      // ATC button
      this.atcButton =
        this.root.querySelector(SELECTORS.atcButtonInRoot) ||
        (this.form ? this.form.querySelector(SELECTORS.atcButtonInForm) : null);

      this.variantTextarea = document.querySelector(SELECTORS.variantJsonTextarea);

      this.removedKey = null;
      this.SOFT_REMOVABLE = new Set(['sockel','trittschall','kleber6','kleber14']);
      this.addonsKey = null;

      this.state = {
        m2: 1,
        verschnitt: false,
        pakete: 0,
        paketPriceCents: 0,
        floorUnitPricePerM2Cents: 0,
        floorTotalCents: 0,
        lfm: 0,
        sockelQty: 0,
        sockelTotalCents: 0,
        sockelUnitPricePerLfmCents: 0,
        trittM2: 0,
        trittPakete: 0,
        trittTotalCents: 0,
        trittPackageSizeM2: 0,
        trittUnitPricePerM2Cents: 0,
        kleber6Qty: 0,
        kleber6TotalCents: 0,
        kleber14Qty: 0,
        kleber14TotalCents: 0,
        addonsTotalCents: 0,
        addonsCompareTotalCents: 0,
        addonsSaveCents: 0,
        grandTotalCents: 0,
        floorSaveCents: 0,
        sockelSaveCents: 0,
        trittSaveCents: 0,
        kleber6SaveCents: 0,
        kleber14SaveCents: 0,
        floorSavePct: 0,
        sockelSavePct: 0,
        trittSavePct: 0,
        kleber6SavePct: 0,
        kleber14SavePct: 0,
        savingsCents: 0,
        floorCompareTotalCents: 0,
        sockelCompareTotalCents: 0,
        trittCompareTotalCents: 0,
        kleber6CompareTotalCents: 0,
        kleber14CompareTotalCents: 0,
        floorCompareAtUnitCents: this.floorCompareAtCents || 0,
        compareAllTotalCents: 0,
      };
    }

    // ✅ DOM fallback prices (Impulse)
    getDomPriceCents() {
      const scope = this.root.closest('.product-single') || document;
      const el = scope.querySelector(SELECTORS.domPrice);
      return parseMoneyToCents(el ? el.textContent : '');
    }

    getDomCompareAtCents() {
      const scope = this.root.closest('.product-single') || document;
      const el = scope.querySelector(SELECTORS.domCompare);
      return parseMoneyToCents(el ? el.textContent : '');
    }

    // ✅ normalize currentVariant prices to cents
    normalizeCurrentVariantPrices() {
      if (!this.currentVariant) return;

      const domPrice = this.getDomPriceCents();
      const domCompare = this.getDomCompareAtCents();

      const vPrice = parseMoneyToCents(this.currentVariant.price);
      const vCompare = parseMoneyToCents(this.currentVariant.compare_at_price);

      this.currentVariant.price = vPrice || this.initialVariantPriceCents || domPrice || 0;
      this.currentVariant.compare_at_price =
        vCompare || this.initialVariantCompareAtCents || this.floorCompareAtCents || domCompare || 0;

      // keep class-level compare consistent
      this.floorCompareAtCents = this.currentVariant.compare_at_price || this.floorCompareAtCents || 0;
      this.state.floorCompareAtUnitCents = this.floorCompareAtCents || 0;
    }

    init() {
      if (!this.m2Input) return;

      if (this.qtyInput) {
        this.qtyInput.min = '0';
        this.qtyInput.value = '0';
      }

      this.m2Input.value = '1';
      if (this.lfmInput) this.lfmInput.value = '0';
      if (this.trittM2Input) this.trittM2Input.value = '0';
      if (this.kleber6QtyInput) this.kleber6QtyInput.value = '0';
      if (this.kleber14QtyInput) this.kleber14QtyInput.value = '0';
      if (this.verschnittInput) this.verschnittInput.checked = false;

      this.currentVariant = this.getCurrentVariantSafe() || {
        id: this.initialVariantId,
        price: this.initialVariantPriceCents || 0,
        compare_at_price: this.initialVariantCompareAtCents || this.floorCompareAtCents || 0
      };

      // ✅ critical: normalize cents + DOM fallback
      this.normalizeCurrentVariantPrices();

      this.removedKey = this.makeRemovedKey(this.currentVariant?.id || this.initialVariantId);
      this.addonsKey = this.makeAddonsKey(this.currentVariant?.id || this.initialVariantId);

      this.accessoriesCatalog = this.readAccessoriesCatalog();
      this.accessoriesByVariant = this.indexAccessoriesByVariant(this.accessoriesCatalog);

      this.addonUndoKeySockel = this.makeAddonsUndoKey(this.currentVariant?.id || this.initialVariantId, 'sockel');
      this.addonUndoKeyVerlege = this.makeAddonsUndoKey(this.currentVariant?.id || this.initialVariantId, 'verlege');

      this.applyAddonsFromStorage();
      this.applyRemovedFromStorage();

      ['sockel','trittschall','kleber6','kleber14'].forEach(key => {
        this.setSoftRemovedUI(key, false);
      });

      this.applyCategoryVisibility();

      this.recomputeAndRender({ source: 'init', syncQty: true, syncAllFromM2: true });

      this.bindControlButtons();
      this.bindInputs();
      this.bindVariantChanges();

      this.bindFormIntercept();
      this.bindAtcClickFallback();

      this.bindQtyObserver();
      this.bindModalEvents();

      this.updateAtcState();
    }

    // ---------- Accessories catalog (real products) ----------
    readAccessoriesCatalog() {
      const el = this.root.querySelector(SELECTORS.accessoriesJson);
      if (!el) return { sockel: [], verlege: [] };
      const raw = (el.textContent || '').trim();
      const parsed = raw ? safeJsonParse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return { sockel: [], verlege: [] };
      return {
        sockel: Array.isArray(parsed.sockel) ? parsed.sockel : [],
        verlege: Array.isArray(parsed.verlege) ? parsed.verlege : [],
      };
    }

    indexAccessoriesByVariant(cat) {
      const m = new Map();
      ['sockel','verlege'].forEach((t) => {
        (cat[t] || []).forEach((it) => {
          const vid = Number(it.variantId || 0);
          if (vid) m.set(String(vid), { ...it, type: t });
        });
      });
      return m;
    }

    makeAddonsUndoKey(variantId, type) {
      const pid = this.productId || '0';
      const vid = variantId || '0';
      return `fc_addons_undo:${pid}:${vid}:${String(type || '')}`;
    }

    makeAddonsKey(variantId) {
      const pid = this.productId || '0';
      const vid = variantId || '0';
      return `fc_addons:${pid}:${vid}`;
    }

    normalizeAddonsBlock(value) {
      if (!value) return {};
      if (Array.isArray(value)) {
        const obj = {};
        value.map(String).forEach((vid) => { obj[String(vid)] = 1; });
        return obj;
      }
      if (typeof value === 'object') {
        const obj = {};
        Object.keys(value).forEach((k) => {
          const q = Number(value[k]);
          const qq = Number.isFinite(q) ? Math.max(0, Math.floor(q)) : 0;
          if (qq > 0) obj[String(k)] = qq;
        });
        return obj;
      }
      return {};
    }

    readAddonsStore() {
      if (!this.addonsKey) return { sockel: {}, verlege: {} };
      const raw = localStorage.getItem(this.addonsKey);
      const parsed = raw ? safeJsonParse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return { sockel: {}, verlege: {} };
      return {
        sockel: this.normalizeAddonsBlock(parsed.sockel),
        verlege: this.normalizeAddonsBlock(parsed.verlege),
      };
    }

    writeAddonsStore(store) {
      if (!this.addonsKey) return;
      const safe = store || { sockel: {}, verlege: {} };
      localStorage.setItem(this.addonsKey, JSON.stringify(safe));
    }

    applyAddonsFromStorage() {
      const store = this.readAddonsStore();
      this.renderAddonSummary('sockel', store.sockel);
      this.renderAddonSummary('verlege', store.verlege);

      const hasSockel = store.sockel && Object.keys(store.sockel).length > 0;
      const hasVerlege = store.verlege && Object.keys(store.verlege).length > 0;

      const btnSockelRemove = this.root.querySelector('[data-fc-remove="addons_sockel"]');
      const btnVerlegeRemove = this.root.querySelector('[data-fc-remove="addons_verlege"]');

      if (btnSockelRemove) btnSockelRemove.hidden = !hasSockel;
      if (btnVerlegeRemove) btnVerlegeRemove.hidden = !hasVerlege;
    }

    renderAddonSummary(type, selectedMap) {
      const el = type === 'sockel' ? this.outAddonsSockelSelected : this.outAddonsVerlegeSelected;
      if (!el) return;

      const map = selectedMap && typeof selectedMap === 'object' ? selectedMap : {};
      const ids = Object.keys(map);

      if (!ids.length) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }

      const items = ids
        .map((vid) => {
          const it = this.accessoriesByVariant.get(String(vid));
          if (!it) return null;
          const qty = Number(map[String(vid)] || 0) || 0;
          if (qty <= 0) return null;
          return { it, qty };
        })
        .filter(Boolean);

      if (!items.length) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }

      el.hidden = false;

      el.innerHTML = items.map(({ it, qty }) => {
        const title = String(it.title || '');
        const img = String(it.image || '');
        const unit = Number(it.priceCents || 0) || 0;
        const total = unit * qty;
        const price = formatMoneyFromCents(total, this.currency);

        const imgHtml = img
          ? `<img class="fc-addon-line__img" src="${img}" alt="${title}" loading="lazy">`
          : `<div class="fc-addon-line__imgPh" aria-hidden="true"></div>`;

        return `
          <div class="fc-addon-line">
            ${imgHtml}
            <div class="fc-addon-line__title">${qty}× ${title}</div>
            <div class="fc-addon-line__price">${price}</div>
          </div>
        `;
      }).join('');
    }

    // ---------- Modal ----------
    resolveUnitLabel(item) {
      const title = String(item?.title || '').toLowerCase().trim();
      if (title.includes('montageclips')) return 'Pakete a 30 St.';
      if (title.includes('abstandskeile')) return 'Pakete a 20 St.';
      if (title.includes('innenecke') || title.includes('endkappen') || title.includes('aussenecke') || title.includes('laminatschneider')) return 'St.';
      return 'St.';
    }

    getRealCatalog(type) {
      const t = type === 'sockel' ? 'sockel' : 'verlege';
      const title = t === 'sockel' ? 'Sockelleisten Zubehör' : 'Verlegezubehör';

      const items = (this.accessoriesCatalog && this.accessoriesCatalog[t])
        ? this.accessoriesCatalog[t].map((it) => ({
            id: String(it.variantId),
            title: it.title || '',
            priceCents: Number(it.priceCents || 0) || 0,
            compareAtCents: Number(it.compareAtCents || 0) || 0,
            image: it.image || '',
            unitLabel: it.unitLabel || 'St.'
          }))
        : [];

      return { title, subtitle: '', items };
    }

    openModal(type) {
      if (!this.modal) return;

      const t = type === 'sockel' ? 'sockel' : 'verlege';
      this.activeModalType = t;

      const store = this.readAddonsStore();
      const selectedMap = t === 'sockel' ? store.sockel : store.verlege;

      this.modalQty = new Map();
      Object.keys(selectedMap || {}).forEach((vid) => {
        const q = Number(selectedMap[String(vid)] || 0) || 0;
        if (q > 0) this.modalQty.set(String(vid), q);
      });

      const catalog = this.getRealCatalog(t);
      if (this.modalTitle) this.modalTitle.textContent = catalog.title;
      if (this.modalSubtitle) this.modalSubtitle.textContent = catalog.subtitle;

      this.modalItems = catalog.items.slice();
      this.renderModalGrid();

      if (this.modalSearch) this.modalSearch.value = '';

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('fc-modal-open');
      document.body.classList.add('fc-modal-open');

      this.updateModalHint();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('fc-modal-open');
      document.body.classList.remove('fc-modal-open');
      this.activeModalType = null;
      this.modalItems = [];
      this.modalQty = new Map();
    }

    updateModalHint() {
      if (!this.modalHint) return;
      const count = Array.from(this.modalQty.values()).filter((q) => (Number(q) || 0) > 0).length;
      this.modalHint.textContent = `${count} ausgewählt`;
    }

    getModalItemQty(id) {
      const v = this.modalQty.get(String(id));
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
    }

    setModalItemQty(id, qty) {
      const q = Number(qty);
      const qq = Number.isFinite(q) ? Math.max(0, Math.floor(q)) : 0;
      const key = String(id);
      if (qq <= 0) this.modalQty.delete(key);
      else this.modalQty.set(key, qq);
    }

    updateModalCardUI(cardEl, it) {
      if (!cardEl || !it) return;

      const id = String(it.id);
      const qty = this.getModalItemQty(id);

      cardEl.classList.toggle('is-selected', qty > 0);

      const input = cardEl.querySelector('[data-fc-modal-qty-input]');
      if (input) input.value = String(qty);

      const priceEl = cardEl.querySelector('.fc-modalCard__price');
      if (priceEl) {
        const unit = Number(it.priceCents || 0) || 0;
        const showCents = qty > 0 ? unit * qty : unit;
        priceEl.textContent = formatMoneyFromCents(showCents, this.currency);
      }
    }

    renderModalGrid() {
      if (!this.modalGrid) return;

      const items = this.modalItems || [];

      const cardHtml = (it) => {
        const id = String(it.id);
        const qty = this.getModalItemQty(id);
        const unitLabel = this.resolveUnitLabel(it);

        const unit = Number(it.priceCents || 0) || 0;
        const showCents = qty > 0 ? unit * qty : unit;

        return `
          <div class="fc-modalCard ${qty > 0 ? 'is-selected' : ''}" data-fc-modal-item="${id}">
            <div class="fc-modalCard__img">
              ${it.image ? `<img src="${it.image}" alt="${it.title}">` : `<div class="fc-modalCard__imgPh"></div>`}
            </div>

            <div class="fc-modalCard__meta">
              <div class="fc-modalCard__title">${it.title}</div>
              <div class="fc-modalCard__price">${formatMoneyFromCents(showCents, this.currency)}</div>

              <div class="fc-modalCard__qtyRow">
                <div class="fc-controls fc-controls--modal">
                  <button type="button" class="fc-controls__btn" data-fc-modal-qty-minus="${id}">-</button>
                  <input class="fc-controls__input" type="number" value="${qty}" min="0" step="1" data-fc-modal-qty-input="${id}">
                  <button type="button" class="fc-controls__btn" data-fc-modal-qty-plus="${id}">+</button>
                </div>

                <span class="fc-modalCard__unit">${unitLabel}</span>
              </div>
            </div>
          </div>
        `;
      };

      this.modalGrid.innerHTML = items.map(cardHtml).join('');
    }

    applyModalSelection() {
      const t = this.activeModalType;
      if (!t) return;

      const store = this.readAddonsStore();
      const payload = {};

      this.modalQty.forEach((q, vid) => {
        const qq = Number(q) || 0;
        if (qq > 0) payload[String(vid)] = qq;
      });

      if (t === 'sockel') store.sockel = payload;
      else store.verlege = payload;

      this.writeAddonsStore(store);
      this.applyAddonsFromStorage();

      this.recomputeAndRender({ source: 'addons_apply', syncQty: true, syncAllFromM2: true });
      this.closeModal();
    }

    bindModalEvents() {
      if (!this.modal) return;

      this.modal.addEventListener('click', (e) => {
        const close = e.target.closest(SELECTORS.modalClose);
        if (close) {
          e.preventDefault();
          this.closeModal();
          return;
        }

        if (e.target && e.target.matches && e.target.matches('.fc-modal__backdrop')) {
          e.preventDefault();
          this.closeModal();
          return;
        }

        const minusBtn = e.target.closest('[data-fc-modal-qty-minus]');
        if (minusBtn) {
          e.preventDefault();
          const id = String(minusBtn.getAttribute('data-fc-modal-qty-minus') || '');
          if (!id) return;

          const cur = this.getModalItemQty(id);
          this.setModalItemQty(id, Math.max(0, cur - 1));

          const card = minusBtn.closest('[data-fc-modal-item]');
          const it = (this.modalItems || []).find(x => String(x.id) === String(id));
          this.updateModalCardUI(card, it);
          this.updateModalHint();
          return;
        }

        const plusBtn = e.target.closest('[data-fc-modal-qty-plus]');
        if (plusBtn) {
          e.preventDefault();
          const id = String(plusBtn.getAttribute('data-fc-modal-qty-plus') || '');
          if (!id) return;

          const cur = this.getModalItemQty(id);
          this.setModalItemQty(id, cur + 1);

          const card = plusBtn.closest('[data-fc-modal-item]');
          const it = (this.modalItems || []).find(x => String(x.id) === String(id));
          this.updateModalCardUI(card, it);
          this.updateModalHint();
          return;
        }
      });

      this.modal.addEventListener('input', (e) => {
        const input = e.target && e.target.closest && e.target.closest('[data-fc-modal-qty-input]');
        if (!input) return;

        const id = String(input.getAttribute('data-fc-modal-qty-input') || '');
        if (!id) return;

        const raw = String(input.value || '').replace(',', '.');
        const v = Number(raw);
        const qty = Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;

        this.setModalItemQty(id, qty);

        const card = input.closest('[data-fc-modal-item]');
        const it = (this.modalItems || []).find(x => String(x.id) === String(id));
        this.updateModalCardUI(card, it);
        this.updateModalHint();
      });

      if (this.modalApply) {
        this.modalApply.addEventListener('click', (e) => {
          e.preventDefault();
          this.applyModalSelection();
        });
      }

      document.addEventListener('keydown', (e) => {
        if (!this.modal || this.modal.hidden) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeModal();
        }
      });
    }

    // ---------- Core ----------
    findImpulseProductForm() {
      const closestForm = this.root.closest('.product-single__meta')?.querySelector(SELECTORS.productForm);
      if (closestForm) return closestForm;

      const all = document.querySelectorAll(SELECTORS.productForm);
      return all && all.length ? all[0] : null;
    }

    getCurrentVariantSafe() {
      if (this.variantTextarea) {
        const json = this.variantTextarea.value || this.variantTextarea.textContent || '';
        const parsed = safeJsonParse(json);
        if (parsed && parsed.id) return parsed;
      }

      if (window.meta && window.meta.product && window.meta.product.variants) {
        const v = window.meta.product.variants.find(x => x.id === this.initialVariantId);
        if (v) return v;
      }

      return null;
    }

    makeRemovedKey(variantId) {
      const pid = this.productId || '0';
      const vid = variantId || '0';
      return `fc_removed:${pid}:${vid}`;
    }

    readRemovedSet() {
      if (!this.removedKey) return new Set();
      const raw = localStorage.getItem(this.removedKey);
      const arr = raw ? safeJsonParse(raw) : null;
      if (!Array.isArray(arr)) return new Set();
      return new Set(arr.map(String));
    }

    writeRemovedSet(set) {
      if (!this.removedKey) return;
      localStorage.setItem(this.removedKey, JSON.stringify(Array.from(set)));
    }

    clearRemoved() {
      if (!this.removedKey) return;
      localStorage.removeItem(this.removedKey);
    }

    showRemoveButton(btn) {
      if (!btn) return;
      btn.hidden = false;
      btn.setAttribute('aria-hidden', 'false');
    }

    setSoftRemovedUI(key, isRemoved) {
      const k = String(key || '');
      if (!this.SOFT_REMOVABLE.has(k)) return;

      const map = {
        sockel: this.blockSockel,
        trittschall: this.blockTritt,
        kleber6: this.blockKleber6,
        kleber14: this.blockKleber14,
      };
      const el = map[k];
      if (!el) return;

      if (isRemoved) el.classList.add('is-removed');
      else el.classList.remove('is-removed');

      const totalRows = el.querySelectorAll('[data-fc-row="gesamt"]');
      totalRows.forEach((row) => {
        row.hidden = !!isRemoved;
        row.setAttribute('aria-hidden', String(!!isRemoved));
      });

      const removeBtn = el.querySelector(`[data-fc-remove="${k}"]`);
      const restoreRow = el.querySelector('.fc-restore-row');
      const restoreBtn = el.querySelector(`[data-fc-restore="${k}"]`);

      if (removeBtn) removeBtn.hidden = !!isRemoved;
      if (restoreRow) restoreRow.hidden = !isRemoved;
      if (restoreBtn) restoreBtn.hidden = !isRemoved;
    }

    applyRemovedFromStorage() {
      const removed = this.readRemovedSet();
      const removeBtns = this.root.querySelectorAll(SELECTORS.removeBtn);

      removeBtns.forEach(btn => {
        this.showRemoveButton(btn);
        const key = String(btn.dataset.fcRemove || '');
        if (!key) return;

        if (this.SOFT_REMOVABLE.has(key)) {
          this.setSoftRemovedUI(key, removed.has(key));
        } else {
          if (removed.has(key)) this.hideBlockHard(key);
          else this.showBlockHard(key);
        }
      });

      ['sockel','trittschall','kleber6','kleber14'].forEach(k => this.setSoftRemovedUI(k, removed.has(k)));
    }

    hideBlockHard(key) {
      const k = String(key || '');
      const map = {
        addons_sockel: this.blockAddonsSockel,
        addons_verlege: this.blockAddonsVerlege,
      };
      const el = map[k];
      if (el) el.classList.add('is-collapsed');
    }

    showBlockHard(key) {
      const k = String(key || '');
      const map = {
        addons_sockel: this.blockAddonsSockel,
        addons_verlege: this.blockAddonsVerlege,
      };
      const el = map[k];
      if (el) el.classList.remove('is-collapsed');
    }

    resetRemovedIfNeeded() {
      const pakete = this.getPaketeFromQtyInput();
      const m2 = this.getIntFromInput(this.m2Input);

      if (pakete === 0 || m2 === 0) {
        this.clearRemoved();
        ['sockel','trittschall','kleber6','kleber14'].forEach(k => this.setSoftRemovedUI(k, false));
        ['addons_sockel','addons_verlege'].forEach(k => this.showBlockHard(k));
        this.applyCategoryVisibility();
      }
    }

    getIntFromInput(inputEl) {
      if (!inputEl) return 0;
      const raw = String(inputEl.value || '').replace(',', '.');
      const v = Number(raw);
      return ceilInt(clampNumber(v, 0, 999999));
    }

    setIntToInput(inputEl, value) {
      if (!inputEl) return;
      inputEl.value = String(ceilInt(value));
    }

    getVerschnittEnabled() {
      return !!(this.verschnittInput && this.verschnittInput.checked);
    }

    getPaketeFromQtyInput() {
      if (!this.qtyInput) return ceilInt(this.state?.pakete || 0);
      const v = Number(String(this.qtyInput.value || '0').replace(',', '.'));
      return ceilInt(clampNumber(v, 0, 999999));
    }

    setPaketeToQtyInput(pakete) {
      if (!this.qtyInput) return;
      this.qtyInput.value = String(ceilInt(pakete));
      this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    isKlebevinyl() {
      const art = this.artDesBodens || '';
      const ver = this.verlegeart || '';
      const isVinyl = textIncludes(art, 'vinyl');
      const isKleb = textIncludes(ver, 'kleb');
      return isVinyl && isKleb;
    }

    applyCategoryVisibility() {
      const showTritt = !this.isKlebevinyl() && !this.hasIntegratedTritt;
      this.showTritt = showTritt;

      if (this.blockTritt) this.blockTritt.classList.toggle('is-collapsed', !showTritt);

      if (!showTritt && this.trittM2Input) {
        this.isSyncing = true;
        this.setIntToInput(this.trittM2Input, 0);
        this.isSyncing = false;
      }

      if (this.blockKleber) this.blockKleber.classList.add('is-collapsed');

      if (this.blockSockel) this.blockSockel.classList.remove('is-collapsed');
      if (this.blockAddonsSockel) this.blockAddonsSockel.classList.remove('is-collapsed');
      if (this.blockAddonsVerlege) this.blockAddonsVerlege.classList.remove('is-collapsed');

      this.updateGlueVisibility(this.readRemovedSet());
    }

    updateGlueVisibility(removedSet) {
      if (!this.blockKleber) return;

      const collapseEl = (el, collapse) => {
        if (!el) return;
        if (collapse) el.classList.add('is-collapsed');
        else el.classList.remove('is-collapsed');
      };

      if (!this.isKlebevinyl()) {
        collapseEl(this.blockKleber, true);
        collapseEl(this.blockKleber6, true);
        collapseEl(this.blockKleber14, true);
        return;
      }

      const removed = removedSet || this.readRemovedSet();

      const has6 = !!this.kleber6VariantId;
      const has14 = !!this.kleber14VariantId;

      const isRemoved6 = removed.has('kleber6');
      const isRemoved14 = removed.has('kleber14');

      const q6 = Number(this.state.kleber6Qty) || 0;
      const q14 = Number(this.state.kleber14Qty) || 0;

      const show6 = has6 && ((q6 > 0 && !isRemoved6) || isRemoved6);
      const show14 = has14 && ((q14 > 0 && !isRemoved14) || isRemoved14);

      collapseEl(this.blockKleber6, !show6);
      collapseEl(this.blockKleber14, !show14);
      collapseEl(this.blockKleber, !(show6 || show14));

      this.setSoftRemovedUI('kleber6', isRemoved6);
      this.setSoftRemovedUI('kleber14', isRemoved14);
    }

    // ✅ Derived inputs...
    syncDerivedInputsFromM2({ m2RawInt, removed, source }) {
      const src = String(source || '');

      if (this.lfmInput && !removed.has('sockel')) {
        if (!src.startsWith('lfm_')) {
          const floorV = this.getVerschnittEnabled();
          const rate = Number(this.verschnittRate) || 0.05;
          const shouldApplyVerschnittToSockel = floorV && Math.abs(rate - 0.05) < 0.0001;

          const baseM2 = shouldApplyVerschnittToSockel
            ? ceilInt(m2RawInt * (1 + rate))
            : m2RawInt;

          this.isSyncing = true;
          this.setIntToInput(this.lfmInput, baseM2);
          this.isSyncing = false;
        }
      }

      const showTritt = this.showTritt === true;

      if (this.trittM2Input && showTritt && !removed.has('trittschall')) {
        if (!src.startsWith('tritt_')) {
          this.isSyncing = true;
          this.setIntToInput(this.trittM2Input, m2RawInt);
          this.isSyncing = false;
        }
      } else {
        if (this.trittM2Input && !src.startsWith('tritt_')) {
          this.isSyncing = true;
          this.setIntToInput(this.trittM2Input, 0);
          this.isSyncing = false;
        }
      }

      if (this.isKlebevinyl()) {
        const allowK6 = this.kleber6QtyInput && !removed.has('kleber6') && !!this.kleber6VariantId;
        const allowK14 = this.kleber14QtyInput && !removed.has('kleber14') && !!this.kleber14VariantId;

        if ((allowK6 || allowK14) && !src.startsWith('kleber6_') && !src.startsWith('kleber14_')) {
          const kgPerM2 = DEFAULTS.kleberGPerM2Avg / 1000;
          const targetKg = m2RawInt * kgPerM2;
          const best = computeBestGlueCombo(targetKg, this.kleber6PriceCents, this.kleber14PriceCents);

          this.isSyncing = true;
          if (allowK6) this.setIntToInput(this.kleber6QtyInput, best.q6);
          if (allowK14) this.setIntToInput(this.kleber14QtyInput, best.q14);
          this.isSyncing = false;
        }
      } else {
        if (this.kleber6QtyInput && !src.startsWith('kleber6_')) this.setIntToInput(this.kleber6QtyInput, 0);
        if (this.kleber14QtyInput && !src.startsWith('kleber14_')) this.setIntToInput(this.kleber14QtyInput, 0);
      }
    }

    recomputeAndRender({ source, syncQty }) {
      if (this.isSyncing) return;

      if (!this.currentVariant) {
        this.currentVariant = this.getCurrentVariantSafe() || { id: this.initialVariantId, price: 0, compare_at_price: this.floorCompareAtCents || 0 };
      }
      this.normalizeCurrentVariantPrices();

      const variant = this.currentVariant;
      const pkg = Number(this.packageSize) || 0;

      const removed = this.readRemovedSet();
      const m2RawInt = this.getIntFromInput(this.m2Input);

      this.syncDerivedInputsFromM2({ m2RawInt, removed, source });

      const m2RawInt2 = this.getIntFromInput(this.m2Input);

      const verschnitt = this.getVerschnittEnabled();
      const m2TotalForFloor = verschnitt ? ceilInt(m2RawInt2 * (1 + this.verschnittRate)) : m2RawInt2;

      let pakete = 0;
      if (pkg > 0) pakete = m2TotalForFloor > 0 ? Math.ceil(m2TotalForFloor / pkg) : 0;
      else pakete = this.getPaketeFromQtyInput();

      const paketPriceCents = parseMoneyToCents(variant?.price) || 0;
      const floorTotalCents = pakete > 0 ? (pakete * paketPriceCents) : 0;

      const floorUnitPricePerM2Cents = (pkg > 0 && paketPriceCents > 0) ? Math.round(paketPriceCents / pkg) : 0;

      const lfm = this.getIntFromInput(this.lfmInput);
      const lfmPer = Number(this.sockelLfmPerStueck) || 0;
      const sockelQty = (!lfmPer || lfmPer <= 0) ? 0 : (lfm > 0 ? Math.ceil(lfm / lfmPer) : 0);
      const sockelTotalCents = removed.has('sockel') ? 0 : (sockelQty * (this.sockelPriceCents || 0));
      const sockelUnitPricePerLfmCents = (lfmPer > 0 && (this.sockelPriceCents || 0) > 0) ? Math.round((this.sockelPriceCents || 0) / lfmPer) : 0;

      const showTritt = this.showTritt === true;
      const trittM2 = showTritt ? this.getIntFromInput(this.trittM2Input) : 0;

      let trittPakete = 0;
      let trittTotalCents = 0;

      const trPkg = Number(this.trittPkgM2) || 0;
      const trMode = String(this.trittMode || 'pack').trim();
      const trUnitM2 = Number(this.trittPacklikeM2) || 0;

      if (!showTritt || removed.has('trittschall') || trittM2 <= 0) {
        trittPakete = 0;
        trittTotalCents = 0;
      } else if (trMode === 'packlike' && trUnitM2 > 0) {
        trittPakete = Math.ceil(trittM2 / trUnitM2);
        trittTotalCents = trittPakete * (this.trittPackPriceCents || 0);
      } else {
        trittPakete = (!trPkg || trPkg <= 0) ? 0 : Math.ceil(trittM2 / trPkg);
        trittTotalCents = trittPakete * (this.trittPackPriceCents || 0);
      }

      const trittPackageSizeM2 = (trMode === 'packlike' && trUnitM2 > 0) ? trUnitM2 : trPkg;
      const trittUnitPricePerM2Cents =
        (trittPackageSizeM2 > 0 && (this.trittPackPriceCents || 0) > 0)
          ? Math.round((this.trittPackPriceCents || 0) / trittPackageSizeM2)
          : 0;

      const kleber6Qty = this.getIntFromInput(this.kleber6QtyInput);
      const kleber14Qty = this.getIntFromInput(this.kleber14QtyInput);

      const kleber6TotalCents = removed.has('kleber6') ? 0 : (kleber6Qty * (this.kleber6PriceCents || 0));
      const kleber14TotalCents = removed.has('kleber14') ? 0 : (kleber14Qty * (this.kleber14PriceCents || 0));

      const storeAddons = this.readAddonsStore();

      const sumByMap = (obj) => {
        const map = obj && typeof obj === 'object' ? obj : {};
        let total = 0;
        let compare = 0;

        Object.keys(map).forEach((vid) => {
          const qty = Number(map[String(vid)] || 0) || 0;
          if (qty <= 0) return;

          const it = this.accessoriesByVariant.get(String(vid));
          if (!it) return;

          total += (Number(it.priceCents || 0) || 0) * qty;
          compare += (Number(it.compareAtCents || 0) || 0) * qty;
        });

        return { total, compare };
      };

      const sockelAdd = sumByMap(storeAddons.sockel);
      const verlegeAdd = sumByMap(storeAddons.verlege);

      const addonsTotalCents = sockelAdd.total + verlegeAdd.total;
      const addonsCompareTotalCents = sockelAdd.compare + verlegeAdd.compare;
      const addonsSaveCents = (addonsCompareTotalCents > addonsTotalCents) ? (addonsCompareTotalCents - addonsTotalCents) : 0;

      const grandTotalCents =
        floorTotalCents +
        sockelTotalCents +
        trittTotalCents +
        kleber6TotalCents +
        kleber14TotalCents +
        addonsTotalCents;

      const floorCompareAtUnit = parseMoneyToCents(variant?.compare_at_price) || this.floorCompareAtCents || 0;
      const floorCompareTotal = floorCompareAtUnit * pakete;
      const floorSave = (floorCompareTotal > floorTotalCents) ? (floorCompareTotal - floorTotalCents) : 0;

      const sockelCompareTotal = (!removed.has('sockel')) ? (this.sockelCompareAtCents * sockelQty) : 0;
      const sockelSave = (sockelCompareTotal > sockelTotalCents) ? (sockelCompareTotal - sockelTotalCents) : 0;

      const trittCompareTotal = (!showTritt || removed.has('trittschall')) ? 0 : (this.trittCompareAtCents * trittPakete);
      const trittSave = (trittCompareTotal > trittTotalCents) ? (trittCompareTotal - trittTotalCents) : 0;

      const kleber6CompareTotal = (!removed.has('kleber6')) ? (this.kleber6CompareAtCents * kleber6Qty) : 0;
      const kleber6Save = (kleber6CompareTotal > kleber6TotalCents) ? (kleber6CompareTotal - kleber6TotalCents) : 0;

      const kleber14CompareTotal = (!removed.has('kleber14')) ? (this.kleber14CompareAtCents * kleber14Qty) : 0;
      const kleber14Save = (kleber14CompareTotal > kleber14TotalCents) ? (kleber14CompareTotal - kleber14TotalCents) : 0;

      const calcPct = (compare, final) => {
        const ca = Number(compare) || 0;
        const fin = Number(final) || 0;
        if (!ca || ca <= fin) return 0;
        return Math.round(((ca - fin) / ca) * 100);
      };

      const floorSavePct = calcPct(floorCompareTotal, floorTotalCents);
      const sockelSavePct = calcPct(sockelCompareTotal, sockelTotalCents);
      const trittSavePct = calcPct(trittCompareTotal, trittTotalCents);
      const kleber6SavePct = calcPct(kleber6CompareTotal, kleber6TotalCents);
      const kleber14SavePct = calcPct(kleber14CompareTotal, kleber14TotalCents);

      const savingsCents = floorSave + sockelSave + trittSave + kleber6Save + kleber14Save + addonsSaveCents;
      const compareAllTotal =
        floorCompareTotal +
        sockelCompareTotal +
        trittCompareTotal +
        kleber6CompareTotal +
        kleber14CompareTotal +
        addonsCompareTotalCents;

      this.state = {
        ...this.state,
        m2: m2RawInt2,
        verschnitt,
        pakete,
        paketPriceCents,
        floorUnitPricePerM2Cents,
        floorTotalCents,
        lfm,
        sockelQty,
        sockelTotalCents,
        sockelUnitPricePerLfmCents,
        trittM2,
        trittPakete,
        trittTotalCents,
        trittPackageSizeM2,
        trittUnitPricePerM2Cents,
        kleber6Qty,
        kleber6TotalCents,
        kleber14Qty,
        kleber14TotalCents,
        addonsTotalCents,
        addonsCompareTotalCents,
        addonsSaveCents,
        grandTotalCents,
        floorSaveCents: floorSave,
        sockelSaveCents: sockelSave,
        trittSaveCents: trittSave,
        kleber6SaveCents: kleber6Save,
        kleber14SaveCents: kleber14Save,
        floorSavePct,
        sockelSavePct,
        trittSavePct,
        kleber6SavePct,
        kleber14SavePct,
        savingsCents,
        floorCompareTotalCents: floorCompareTotal,
        sockelCompareTotalCents: sockelCompareTotal,
        trittCompareTotalCents: trittCompareTotal,
        kleber6CompareTotalCents: kleber6CompareTotal,
        kleber14CompareTotalCents: kleber14CompareTotal,
        floorCompareAtUnitCents: floorCompareAtUnit,
        compareAllTotalCents: compareAllTotal,
      };

      if (syncQty && pkg > 0) {
        this.isSyncing = true;
        this.setPaketeToQtyInput(pakete);
        this.isSyncing = false;
      }

      this.setIntToInput(this.m2Input, m2RawInt2);

      this.render();

      this.setSoftRemovedUI('sockel', removed.has('sockel'));
      this.setSoftRemovedUI('trittschall', removed.has('trittschall'));
      this.setSoftRemovedUI('kleber6', removed.has('kleber6'));
      this.setSoftRemovedUI('kleber14', removed.has('kleber14'));

      this.updateGlueVisibility(removed);

      this.resetRemovedIfNeeded();
      this.updateAtcState();
    }

    render() {
      const s = this.state;
      const pkg = Number(this.packageSize) || 0;

      const setFloorUvpPerM2Only = (el) => {
        if (!el) return;

        const caUnit = Number(s.floorCompareAtUnitCents) || 0;
        const finPerM2 = Number(s.floorUnitPricePerM2Cents) || 0;

        if (!pkg || pkg <= 0 || !caUnit || caUnit <= 0 || !finPerM2 || finPerM2 <= 0) {
          el.hidden = true;
          el.textContent = '';
          return;
        }

        const uvpPerM2Cents = Math.round(caUnit / pkg);
        const show = uvpPerM2Cents > finPerM2;

        if (!show) {
          el.hidden = true;
          el.textContent = '';
          return;
        }

        el.hidden = false;
        el.textContent = `UVP ${formatMoneyFromCents(uvpPerM2Cents, this.currency)}`;
      };

      const setSaveLine = (eurEl, pct, saveCents) => {
        if (!eurEl) return;
        const sc = Number(saveCents) || 0;
        const p = Number(pct) || 0;
        if (!sc || sc <= 0) { eurEl.textContent = formatMoneyFromCents(0, this.currency); return; }
        eurEl.textContent = `${p}%: ${formatMoneyFromCents(sc, this.currency)}`;
      };

      if (this.outPakete) this.outPakete.textContent = String(s.pakete);
      if (this.outPackageSize) this.outPackageSize.textContent = pkg > 0 ? formatNumber(pkg, 2) : '0';

      if (this.outPaketPrice) this.outPaketPrice.textContent = formatMoneyFromCents(s.paketPriceCents || 0, this.currency);

      if (this.outFloorUnitPrice) this.outFloorUnitPrice.textContent = formatMoneyFromCents(s.floorUnitPricePerM2Cents || 0, this.currency);
      if (this.outSockelUnitPrice) this.outSockelUnitPrice.textContent = formatMoneyFromCents(s.sockelUnitPricePerLfmCents || 0, this.currency);
      if (this.outTrittUnitPrice) this.outTrittUnitPrice.textContent = formatMoneyFromCents(s.trittUnitPricePerM2Cents || 0, this.currency);
      if (this.outKleber6UnitPrice) this.outKleber6UnitPrice.textContent = formatMoneyFromCents(this.kleber6PriceCents || 0, this.currency);
      if (this.outKleber14UnitPrice) this.outKleber14UnitPrice.textContent = formatMoneyFromCents(this.kleber14PriceCents || 0, this.currency);

      if (this.outTrittPackageSize) {
        const v = Number(s.trittPackageSizeM2) || 0;
        this.outTrittPackageSize.textContent = v > 0 ? formatNumber(v, 2) : '0';
      }
      if (this.outTrittPaketPrice) {
        this.outTrittPaketPrice.textContent = formatMoneyFromCents(this.trittPackPriceCents || 0, this.currency);
      }

      if (this.outFloorTotal) this.outFloorTotal.textContent = formatMoneyFromCents(s.floorTotalCents || 0, this.currency);
      if (this.outSockelTotal) this.outSockelTotal.textContent = formatMoneyFromCents(s.sockelTotalCents || 0, this.currency);
      if (this.outTrittTotal) this.outTrittTotal.textContent = formatMoneyFromCents(s.trittTotalCents || 0, this.currency);
      if (this.outKleber6Total) this.outKleber6Total.textContent = formatMoneyFromCents(s.kleber6TotalCents || 0, this.currency);
      if (this.outKleber14Total) this.outKleber14Total.textContent = formatMoneyFromCents(s.kleber14TotalCents || 0, this.currency);

      if (this.outFloorBlockTotal) this.outFloorBlockTotal.textContent = formatMoneyFromCents(s.floorTotalCents || 0, this.currency);
      if (this.outSockelBlockTotal) this.outSockelBlockTotal.textContent = formatMoneyFromCents(s.sockelTotalCents || 0, this.currency);
      if (this.outTrittBlockTotal) this.outTrittBlockTotal.textContent = formatMoneyFromCents(s.trittTotalCents || 0, this.currency);
      if (this.outKleber6BlockTotal) this.outKleber6BlockTotal.textContent = formatMoneyFromCents(s.kleber6TotalCents || 0, this.currency);
      if (this.outKleber14BlockTotal) this.outKleber14BlockTotal.textContent = formatMoneyFromCents(s.kleber14TotalCents || 0, this.currency);

      if (this.outGrandTotal) this.outGrandTotal.textContent = formatMoneyFromCents(s.grandTotalCents || 0, this.currency);

      if (this.outGrandCompareTotal) {
        const ca = Number(s.compareAllTotalCents) || 0;
        const fin = Number(s.grandTotalCents) || 0;
        const show = ca > fin && fin > 0;
        if (!show) {
          this.outGrandCompareTotal.hidden = true;
          this.outGrandCompareTotal.textContent = '';
        } else {
          this.outGrandCompareTotal.hidden = false;
          this.outGrandCompareTotal.textContent = formatMoneyFromCents(ca, this.currency);
        }
      }

      if (this.outSockelQty) this.outSockelQty.textContent = String(s.sockelQty || 0);
      if (this.outTrittPakete) this.outTrittPakete.textContent = String(s.trittPakete || 0);
      if (this.outKleber6Qty) this.outKleber6Qty.textContent = String(s.kleber6Qty || 0);
      if (this.outKleber14Qty) this.outKleber14Qty.textContent = String(s.kleber14Qty || 0);

      setFloorUvpPerM2Only(this.outFloorUvp);

      [this.outSockelUvp, this.outTrittUvp, this.outKleber6Uvp, this.outKleber14Uvp].forEach((el) => {
        if (!el) return;
        el.hidden = true;
        el.textContent = '';
      });

      setSaveLine(this.outFloorSaveEur, s.floorSavePct, s.floorSaveCents);
      setSaveLine(this.outSockelSaveEur, s.sockelSavePct, s.sockelSaveCents);
      setSaveLine(this.outTrittSaveEur, s.trittSavePct, s.trittSaveCents);
      setSaveLine(this.outKleber6SaveEur, s.kleber6SavePct, s.kleber6SaveCents);
      setSaveLine(this.outKleber14SaveEur, s.kleber14SavePct, s.kleber14SaveCents);

      if (this.outSavings) this.outSavings.textContent = formatMoneyFromCents(s.savingsCents || 0, this.currency);
    }

    updateAtcState() {
      if (!this.atcButton) return;
      const qty = ceilInt(this.state?.pakete || 0);
      const canAdd = qty > 0;
      this.atcButton.disabled = !canAdd;
      this.atcButton.setAttribute('aria-disabled', String(!canAdd));
    }

    bindControlButtons() {
      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest(SELECTORS.actionBtn);
        if (!btn) return;

        const action = String(btn.dataset.fcAction || '');

        if (action === 'add_sockel_accessories') {
          e.preventDefault(); e.stopPropagation();
          this.openModal('sockel');
          return;
        }
        if (action === 'add_verlege_accessories') {
          e.preventDefault(); e.stopPropagation();
          this.openModal('verlege');
          return;
        }

        const LOCKED_ACTIONS = new Set([
          'lfm_minus','lfm_plus',
          'tritt_minus','tritt_plus',
          'kleber6_minus','kleber6_plus',
          'kleber14_minus','kleber14_plus',
        ]);

        if (LOCKED_ACTIONS.has(action)) {
          e.preventDefault(); e.stopPropagation();
          return;
        }

        const incDec = (inputEl, dir, step = 1) => {
          if (!inputEl) return;
          e.preventDefault();
          const cur = this.getIntFromInput(inputEl);
          const next = dir === 'plus' ? cur + step : cur - step;
          this.setIntToInput(inputEl, Math.max(0, next));
        };

        if (action === 'm2_minus') { incDec(this.m2Input, 'minus', 1); this.recomputeAndRender({ source: 'm2_minus', syncQty: true }); }
        if (action === 'm2_plus')  { incDec(this.m2Input, 'plus', 1);  this.recomputeAndRender({ source: 'm2_plus',  syncQty: true }); }
      });

      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest(SELECTORS.removeBtn);
        if (!btn) return;

        e.preventDefault();
        const key = String(btn.dataset.fcRemove || '').trim();
        if (!key) return;

        if (key === 'addons_sockel' || key === 'addons_verlege') {
          const t = key === 'addons_sockel' ? 'sockel' : 'verlege';

          const store = this.readAddonsStore();
          if (t === 'sockel') store.sockel = {};
          else store.verlege = {};

          this.writeAddonsStore(store);
          this.applyAddonsFromStorage();

          this.recomputeAndRender({ source: 'addons_remove', syncQty: true });
          return;
        }

        const removed = this.readRemovedSet();
        removed.add(key);
        this.writeRemovedSet(removed);

        if (this.SOFT_REMOVABLE.has(key)) this.setSoftRemovedUI(key, true);
        else this.hideBlockHard(key);

        this.recomputeAndRender({ source: 'remove', syncQty: true });
      });

      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest(SELECTORS.restoreBtn);
        if (!btn) return;

        e.preventDefault();
        const key = String(btn.dataset.fcRestore || '').trim();
        if (!key) return;

        const removed = this.readRemovedSet();
        removed.delete(key);
        this.writeRemovedSet(removed);

        if (this.SOFT_REMOVABLE.has(key)) this.setSoftRemovedUI(key, false);
        else this.showBlockHard(key);

        this.recomputeAndRender({ source: 'restore', syncQty: true });
      });
    }

    bindInputs() {
      const bindIntInput = (inputEl, onChange) => {
        if (!inputEl) return;
        inputEl.addEventListener('input', () => onChange());
        inputEl.addEventListener('change', () => {
          const v = this.getIntFromInput(inputEl);
          this.setIntToInput(inputEl, v);
          onChange();
        });
      };

      bindIntInput(this.m2Input, () => this.recomputeAndRender({ source: 'm2_input', syncQty: true }));

      if (this.verschnittInput) {
        this.verschnittInput.addEventListener('change', () => {
          this.recomputeAndRender({ source: 'verschnitt', syncQty: true });
        });
      }

      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => this.recomputeAndRender({ source: 'qty', syncQty: false }));
        this.qtyInput.addEventListener('change', () => this.recomputeAndRender({ source: 'qty', syncQty: false }));
      }
    }

    bindVariantChanges() {
      if (this.variantTextarea) {
        let last = this.variantTextarea.value;
        window.setInterval(() => {
          const cur = this.variantTextarea.value;
          if (cur !== last) { last = cur; this.onVariantUpdated(); }
        }, 350);
      }

      const handler = () => this.onVariantUpdated();
      document.addEventListener('variantChange', handler);
      document.addEventListener('variantSKUChange', handler);
      document.addEventListener('variant:change', handler);
    }

    onVariantUpdated() {
      const v = this.getCurrentVariantSafe();
      if (!v || !v.id) return;

      this.currentVariant = v;
      this.normalizeCurrentVariantPrices();

      this.removedKey = this.makeRemovedKey(v.id);
      this.addonsKey = this.makeAddonsKey(v.id);

      this.accessoriesCatalog = this.readAccessoriesCatalog();
      this.accessoriesByVariant = this.indexAccessoriesByVariant(this.accessoriesCatalog);

      this.addonUndoKeySockel = this.makeAddonsUndoKey(v.id, 'sockel');
      this.addonUndoKeyVerlege = this.makeAddonsUndoKey(v.id, 'verlege');

      this.applyAddonsFromStorage();
      this.applyRemovedFromStorage();
      this.applyCategoryVisibility();

      this.recomputeAndRender({ source: 'variant', syncQty: true });
    }

    bindQtyObserver() {
      if (!this.form) return;

      const obs = new MutationObserver(() => {
        const q = this.form.querySelector(SELECTORS.qtyInput);
        if (q && q !== this.qtyInput) {
          this.qtyInput = q;
          this.qtyInput.min = '0';
          this.bindInputs();
          this.recomputeAndRender({ source: 'qty', syncQty: false });
        }
      });

      obs.observe(this.form, { childList: true, subtree: true });
    }

    bindFormIntercept() {
      if (!this.form) return;

      this.form.addEventListener('submit', async (e) => {
        if (!this.root || !document.body.contains(this.root)) return;

        const pakete = this.getPaketeFromQtyInput();
        if (pakete <= 0) {
          e.preventDefault(); e.stopPropagation();
          this.updateAtcState();
          return;
        }

        e.preventDefault(); e.stopPropagation();

        try {
          await this.addToCartBatch();
          window.setTimeout(() => { openMiniCart(); }, 0);
        } catch (err) {
          console.error('[floor-calculator] addToCart failed:', err);
          alert('Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
          if (this.atcButton) {
            this.atcButton.disabled = false;
            this.atcButton.classList.remove('is-loading', 'btn--loading');
          }
        }
      }, true);
    }

    bindAtcClickFallback() {
      if (!this.atcButton) return;

      const isInsideForm = !!(this.form && this.form.contains(this.atcButton));
      if (isInsideForm) return;

      this.atcButton.addEventListener('click', async (e) => {
        const pakete = this.getPaketeFromQtyInput();
        if (pakete <= 0) {
          e.preventDefault(); e.stopPropagation();
          this.updateAtcState();
          return;
        }

        e.preventDefault(); e.stopPropagation();

        try {
          await this.addToCartBatch();
          window.setTimeout(() => { openMiniCart(); }, 0);
        } catch (err) {
          console.error('[floor-calculator] addToCart failed:', err);
          alert('Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
          if (this.atcButton) {
            this.atcButton.disabled = false;
            this.atcButton.classList.remove('is-loading', 'btn--loading');
          }
        }
      }, true);
    }

    // ---------- BUILD ITEMS ----------
    buildCartItems() {
      const removed = this.readRemovedSet();
      const items = [];

      const v = this.currentVariant || this.getCurrentVariantSafe();
      const mainVariantId = Number(v?.id || this.initialVariantId) || 0;
      const pakete = this.getPaketeFromQtyInput();

      // ✅ bundle binding props for ALL calc lines
      const fcBase = {
        _fc: '1',
        _fc_pid: String(this.productId || this.handle || ''),
        _fc_parent: String(this.bundleId || ''),
        _fc_bundle: String(this.bundleId || ''),
      };

      if (mainVariantId && pakete > 0) {
        const m2Raw = this.getIntFromInput(this.m2Input);
        const verschnitt = this.getVerschnittEnabled();
        const m2Total = verschnitt ? ceilInt(m2Raw * (1 + this.verschnittRate)) : m2Raw;

        // Store DELIVERED coverage (pakete × packageSize) with fractional precision
        // so the cart's "Entspricht" line reflects the real m² the customer gets.
        const pkgSize = Number(this.packageSize) || 0;
        const deliveredM2 = pkgSize > 0 ? roundTo(pakete * pkgSize, 2) : 0;

        const props = {
          ...fcBase,
          _fc_role: 'main',
          _fc_kind: 'floor',
          _fc_unit: 'm2',
          _fc_total: String(deliveredM2),
          m2: String(deliveredM2),

          _fc_pkg_m2: String(Number(this.packageSize) || 0),
          _fc_verschnitt_rate: verschnitt ? String(Number(this.verschnittRate) || 0) : '',
          _fc_verschnitt: verschnitt ? '1' : '0',

          _fc_sockel_lfm_per_stueck: String(Number(this.sockelLfmPerStueck) || 0),

          _fc_tritt_mode: String(this.trittMode || 'pack'),
          _fc_tritt_pkg_m2: String(Number(this.trittPkgM2) || 0),
          _fc_tritt_packlike_m2: String(Number(this.trittPacklikeM2) || 0),

          _fc_has_integrated_tritt: this.hasIntegratedTritt ? '1' : '0',
          _fc_is_klebevinyl: this.isKlebevinyl() ? '1' : '0',

          _fc_sockel_vid: String(Number(this.sockelVariantId) || 0),
          _fc_tritt_vid: String(Number(this.trittVariantId) || 0),
          _fc_kleber6_vid: String(Number(this.kleber6VariantId) || 0),
          _fc_kleber14_vid: String(Number(this.kleber14VariantId) || 0),
        };

        if (verschnitt) {
          const pct = Math.round((Number(this.verschnittRate) || 0) * 100);
          props.verschnitt = `${pct}%`;
          props._fc_verschnitt = '1';
          props._fc_verschnitt_rate = String(Number(this.verschnittRate) || 0);
        }

        // GRATIS SOCKEL PROMO: tag this floor line so sockelleisten-promo.js
        // picks it up and auto-attaches a matching Gratis sockel line.
        // Reads _fc_pkg_m2 (already set above) × this line's current quantity
        // on every sync pass → sockel lfm always tracks the live floor m².
        if (this.isPromoEligible) {
          props._role = 'floor';
          props._floor_base = 'promo';
          props._src_collection = this.promoSrcCollection;
        }

        items.push({ id: mainVariantId, quantity: pakete, properties: props });
      }

      // ✅ Sockel
      // GRATIS SOCKEL PROMO: skip the paid-sockel sub-item when the product is
      // promo-eligible. sockelleisten-promo.js auto-attaches the Gratis sockel
      // based on the _role=floor + _floor_base=promo stamps above.
      if (!removed.has('sockel') && this.sockelVariantId && !this.isPromoEligible) {
        const qty = this.state.sockelQty || 0;
        if (qty > 0) {
          // Store DELIVERED lfm (sockelQty × lfmPerStueck) fractional for cart precision.
          const lfmPer = Number(this.sockelLfmPerStueck) || 0;
          const deliveredLfm = lfmPer > 0 ? roundTo(qty * lfmPer, 2) : 0;

          items.push({
            id: this.sockelVariantId,
            quantity: qty,
            properties: {
              ...fcBase,
              _fc_remove_with_main: '1',
              _fc_role: 'dep',
              _fc_kind: 'sockel',
              _fc_unit: 'lfm',
              _fc_total: String(deliveredLfm),
              lfm: String(deliveredLfm),
            }
          });
        }
      }

      // ✅ Trittschall
      const showTritt = this.showTritt === true;
      if (showTritt && !removed.has('trittschall') && this.trittVariantId) {
        const qty = this.state.trittPakete || 0;
        if (qty > 0) {
          const trMode = String(this.trittMode || 'pack').trim();
          const trUnitM2 = Number(this.trittPacklikeM2) || 0;

          // Store DELIVERED m² (trittPakete × trittPackageSize) fractional for cart precision.
          const trPkgSize = (trMode === 'packlike' && trUnitM2 > 0) ? trUnitM2 : (Number(this.trittPkgM2) || 0);
          const deliveredTrittM2 = trPkgSize > 0 ? roundTo(qty * trPkgSize, 2) : 0;

          const props = {
            ...fcBase,
            _fc_remove_with_main: '1',
            _fc_role: 'dep',
            _fc_kind: 'tritt',
            _fc_unit: 'm2',
            _fc_total: String(deliveredTrittM2),
            m2: String(deliveredTrittM2),
          };

          // ✅ packlike support for drawer sync
          if (trMode === 'packlike' && trUnitM2 > 0) {
            props._fc_packlike_m2 = String(trUnitM2);
          }

          items.push({
            id: this.trittVariantId,
            quantity: qty,
            properties: props
          });
        }
      }

      // ✅ Kleber
      if (!removed.has('kleber6') && this.kleber6VariantId) {
        const qty = this.state.kleber6Qty || 0;
        if (qty > 0) items.push({ id: this.kleber6VariantId, quantity: qty, properties: { ...fcBase, _fc_remove_with_main: '1', _fc_role: 'dep', _fc_kind: 'kleber6' } });
      }

      if (!removed.has('kleber14') && this.kleber14VariantId) {
        const qty = this.state.kleber14Qty || 0;
        if (qty > 0) items.push({ id: this.kleber14VariantId, quantity: qty, properties: { ...fcBase, _fc_remove_with_main: '1', _fc_role: 'dep', _fc_kind: 'kleber14' } });
      }

      // MODAL ADDONS
      const store = this.readAddonsStore();

      const addSelected = (obj) => {
        const map = obj && typeof obj === 'object' ? obj : {};
        Object.keys(map).forEach((vid) => {
          const vIdNum = Number(vid || 0);
          const qty = Number(map[String(vid)] || 0) || 0;
          if (!vIdNum || qty <= 0) return;

          // берем данные из каталога, чтобы понять Angebot/скидку
          const catItem = this.accessoriesByVariant.get(String(vIdNum));
          const title = String(catItem?.title || '');
          const isOfferByTitle = /angebot/i.test(title);
          const isDiscounted = (Number(catItem?.compareAtCents || 0) || 0) > (Number(catItem?.priceCents || 0) || 0);

          // именно эти позиции должны удаляться вместе с main
          const removeWithMain = (isOfferByTitle || isDiscounted);

          items.push({
            id: vIdNum,
            quantity: qty,
            properties: {
              ...fcBase,

              // ключевая метка
              _fc_remove_with_main: removeWithMain ? '1' : '0',

              // если Angebot/discounted — делаем это dep, а не addon
              _fc_role: removeWithMain ? 'dep' : 'addon',
              _fc_group: removeWithMain ? 'offer' : 'addon',

              // kind оставим информативным
              _fc_kind: removeWithMain ? 'offer' : 'addon',
            }
          });
        });
      };

      addSelected(store.sockel);
      addSelected(store.verlege);

      return items;
    }

    // ---------- ADD TO CART (UPSERT) ----------
    async addToCartBatch() {
      const desired = this.buildCartItems();
      if (!desired.length) return;

      if (this.atcButton) {
        this.atcButton.disabled = true;
        this.atcButton.classList.add('is-loading', 'btn--loading');
      }

      const cart = await fetchCartJson();

      // ✅ index existing calculator lines by composite signature
      const bySig = new Map();
      const makeSig = (vid, props) => {
        const p = props && typeof props === 'object' ? props : {};
        const parent = String(p._fc_parent || '').trim();
        const pid = String(p._fc_pid || '').trim();
        const role = String(p._fc_role || '').trim();
        const group = String(p._fc_group || '').trim();
        return `${vid}::${parent}::${pid}::${group}::${role}`;
      };

      (cart.items || []).forEach((it) => {
        const vid = Number(it.variant_id || 0);
        if (!vid) return;

        const props = it.properties || {};
        const hasFc = props && String(props._fc || '') === '1';
        if (!hasFc) return;

        const parent = String(props._fc_parent || '').trim();
        const pid = String(props._fc_pid || '').trim();
        const role = String(props._fc_role || '').trim();

        // Require parent+pid for scoping. role can be missing on legacy lines; still index them
        if (!parent || !pid) return;

        const sig = makeSig(vid, props);
        if (!bySig.has(sig)) bySig.set(sig, { key: it.key, hasFc: true });
      });

      const toChange = [];
      const toAdd = [];

      desired.forEach((d) => {
        const vid = Number(d.id || 0);
        if (!vid) return;

        const props = d.properties || {};
        const sig = makeSig(vid, props);
        const existing = bySig.get(sig);

        if (existing && existing.key) {
          toChange.push({ key: existing.key, quantity: Number(d.quantity) || 0, properties: props });
        } else {
          toAdd.push(d);
        }
      });

      for (const ch of toChange) await cartChangeByKey(ch.key, ch.quantity, ch.properties);
      if (toAdd.length) await cartAddItems(toAdd);

      try { document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (_) {}

      if (this.atcButton) {
        this.atcButton.disabled = false;
        this.atcButton.classList.remove('is-loading', 'btn--loading');
      }
    }
  }

  function boot() {
    const root = document.querySelector(SELECTORS.root);
    if (!root) return;

    // ── FC VARIANT GUARD: redirect Muster variants to Produkt ──
    const redirectId = window.__fcRedirectVariantId__;
    if (redirectId) {
      const applyGuard = () => {
        const textarea = document.querySelector('textarea[data-current-variant-json]');
        if (textarea) {
          const allVariants = (window.meta && window.meta.product && window.meta.product.variants) || [];
          const produktVariant = allVariants.find(v => Number(v.id) === Number(redirectId));
          if (produktVariant) {
            if (!produktVariant.compare_at_price) {
              const compareRaw = root.dataset.initialVariantCompareAt;
              if (compareRaw) produktVariant.compare_at_price = compareRaw;
            }
            textarea.value = JSON.stringify(produktVariant);
            textarea.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', redirectId);
        window.history.replaceState({}, '', url.toString());
        delete window.__fcRedirectVariantId__;
      };

      // Подождать пока window.meta.product.variants будет готов
      if (window.meta && window.meta.product && window.meta.product.variants) {
        applyGuard(); // уже готов — сразу
      } else {
        // Ждём с интервалом максимум 10 попыток (1 секунда)
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if ((window.meta && window.meta.product && window.meta.product.variants) || attempts >= 10) {
            clearInterval(interval);
            applyGuard();
          }
        }, 100);
      }
    }
    // ── END FC VARIANT GUARD ──

    // ── Sold-out lockdown ──
    if (root.dataset.fcSoldOut === '1') {
      root.classList.add('fc--sold-out');

      // disable everything inside the calculator
      root.querySelectorAll('button, input, a').forEach(el => {
        el.disabled = true;
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('tabindex', '-1');
      });

      // disable main ATC button (Impulse: inside div.payment-buttons)
      const paymentButtons = document.querySelector('.payment-buttons');
      if (paymentButtons) {
        paymentButtons.querySelectorAll('button').forEach(el => {
          el.disabled = true;
          el.setAttribute('aria-disabled', 'true');
          el.style.opacity = '0.4';
          el.style.pointerEvents = 'none';
          el.style.cursor = 'not-allowed';
        });
      }

      // disable Shopify dynamic payment button (PayPal etc.)
      const shopifyPaymentBtn = document.querySelector('[data-shopify="payment-button"]');
      if (shopifyPaymentBtn) {
        shopifyPaymentBtn.style.opacity = '0.4';
        shopifyPaymentBtn.style.pointerEvents = 'none';
        shopifyPaymentBtn.style.cursor = 'not-allowed';
      }

      // block form submit as safety net
      const productForm = document.querySelector('form.product-single__form');
      if (productForm) {
        productForm.addEventListener('submit', e => {
          e.preventDefault();
          e.stopImmediatePropagation();
        }, true);
      }

      return;
    }

    const fc = new FloorCalculator(root);
    fc.init();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();